import { useState, useEffect } from 'react';
import { Box, Typography, Link } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence as MotionPresence } from 'framer-motion';
import type { FC, ReactNode } from 'react';

import { useMytaverseEvent } from '../../providers';
import { useMytaverse } from '@providers/MytaverseProvider';
import { useStyles } from './styles';

const AnimatePresence = MotionPresence as FC<{ children: ReactNode }>;

const BrandingModeNotification = () => {
  const { t: translate } = useTranslation();
  const {
    isBrandingModeEnabled,
    setIsBrandingModeEnabled,
    setRoomSpots,
    currentRegion,
    aiTriggerName,
  } = useMytaverseEvent();
  const { sendMessageToUnreal } = useMytaverse();
  const classes = useStyles();

  const handleExitBrandingMode = () => {
    setIsBrandingModeEnabled(false);
    setRoomSpots([]);
    sendMessageToUnreal({
      action: 'LEAVE_CUSTOMIZATION_MODE',
    });
  };

  const regionLabel = currentRegion?.region || aiTriggerName;

  const [debouncedRegionLabel, setDebouncedRegionLabel] = useState(regionLabel);
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedRegionLabel(regionLabel);
    }, 500);
    return () => clearTimeout(timer);
  }, [regionLabel]);

  return (
    <AnimatePresence>
      {isBrandingModeEnabled ? (
        <div
          key="branding-banner"
          style={{
            position: 'fixed',
            bottom: 80,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 1300,
          }}
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
          >
            <Box sx={classes.container}>
              <Box sx={classes.row}>
                <Typography sx={classes.text}>
                  {translate(
                    'adminToolbox.brandingModeEnabled',
                    'Branding Mode Enabled',
                  )}
                </Typography>
                <Typography sx={classes.divider}>|</Typography>
                <Link
                  component="button"
                  onClick={handleExitBrandingMode}
                  sx={classes.exitLink}
                >
                  {translate(
                    'adminToolbox.exitBrandingMode',
                    'Exit branding mode',
                  )}
                </Link>
              </Box>
              <AnimatePresence>
                {debouncedRegionLabel ? (
                  <motion.div
                    key={debouncedRegionLabel}
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.2, ease: 'easeOut' }}
                    style={{ overflow: 'hidden' }}
                  >
                    <Box sx={classes.row}>
                      <Typography sx={classes.text}>
                        {translate(
                          'adminToolbox.currentRegion',
                          'Current area',
                        )}
                        : {debouncedRegionLabel}
                      </Typography>
                    </Box>
                  </motion.div>
                ) : null}
              </AnimatePresence>
            </Box>
          </motion.div>
        </div>
      ) : null}
    </AnimatePresence>
  );
};

export default BrandingModeNotification;
